import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAivyContext } from '../../../context'
import Checkbox from '../../checkbox'
import Button from '../../tailwind/Button'

const TRANSLATION_LOCATION = 'career.flex.challenges.'

const Challenges = ({
  entity,
  updateHandler,
  backHandler,
  create,
  update,
  isLoading,
  type
}) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()

  // Parse available challenges from system
  const availableChallenges = useMemo(() => {
    if (!system?.challenges?.data) return []

    const challengesData = JSON.parse(system.challenges.data)
    return Object.values(challengesData)
      .filter((challenge) => challenge.active === true)
      .sort((a, b) => a.title?.de?.localeCompare(b.title?.de) || 0)
  }, [system])

  // Parse current partner challenges
  // If no challenges are configured, all challenges are active by default
  const currentChallenges = useMemo(() => {
    if (!entity.app_settings?.challenges) {
      // No challenges configured = all challenges are active
      return availableChallenges.map((challenge) => challenge.exam_id)
    }

    try {
      const challenges = JSON.parse(entity.app_settings.challenges)
      return challenges.map((challenge) => challenge.exam_id)
    } catch (error) {
      console.error('Error parsing partner challenges:', error)
      // On error, default to all challenges active
      return availableChallenges.map((challenge) => challenge.exam_id)
    }
  }, [entity.app_settings?.challenges, availableChallenges])

  // Initialize form with current challenges
  const initialValues = useMemo(() => {
    const values = {}
    availableChallenges.forEach((challenge) => {
      values[challenge.exam_id] = currentChallenges.includes(challenge.exam_id)
    })
    return values
  }, [availableChallenges, currentChallenges])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: (values) => {
      // Convert form values to challenges array
      const selectedChallenges = Object.entries(values)
        .filter(([_, isSelected]) => isSelected)
        .map(([exam_id]) => ({ exam_id }))

      updateHandler({
        challenges: JSON.stringify(selectedChallenges)
      })
    }
  })

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='space-y-4'>
        {availableChallenges.length === 0 ? (
          <div className='py-8 text-center'>
            <p className='text-gray-500'>
              {t(TRANSLATION_LOCATION + 'no_challenges_available')}
            </p>
          </div>
        ) : (
          <div className='space-y-3'>
            {availableChallenges.map((challenge) => (
              <div
                key={challenge.exam_id}
                className='flex items-start space-x-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50'
              >
                <Checkbox
                  id={challenge.exam_id}
                  checked={formik.values[challenge.exam_id] || false}
                  onChange={formik.handleChange}
                />
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center space-x-3'>
                    {challenge.card?.image && (
                      <img
                        src={challenge.card.image}
                        alt={challenge.title?.de || challenge.exam_id}
                        className='h-10 w-10 rounded-full object-contain p-1'
                        style={{
                          backgroundColor:
                            challenge.backgroundColor || '#f3f4f6'
                        }}
                      />
                    )}
                    <div>
                      <label
                        htmlFor={challenge.exam_id}
                        className='cursor-pointer text-sm font-medium text-gray-900'
                      >
                        {challenge.title?.de || challenge.exam_id}
                      </label>
                      {challenge.measuring?.de && (
                        <p className='mt-1 text-xs text-gray-600'>
                          {challenge.measuring.de}
                        </p>
                      )}
                      {challenge.timeToComplete && (
                        <p className='mt-1 text-xs text-gray-500'>
                          {t(TRANSLATION_LOCATION + 'duration', {
                            minutes: Math.round(
                              challenge.timeToComplete / 60000
                            )
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='mt-8 flex justify-between'>
        <Button.SecondaryLG
          text={t('career.flex.back_action')}
          onClick={backHandler}
          disabled={isLoading}
        />
        <Button.PrimaryLG
          text={t(
            create ? 'career.flex.next_action' : 'career.flex.save_action'
          )}
          type='submit'
          disabled={isLoading}
          isLoading={isLoading}
        />
      </div>
    </form>
  )
}

Challenges.propTypes = {
  entity: PropTypes.object.isRequired,
  updateHandler: PropTypes.func.isRequired,
  backHandler: PropTypes.func.isRequired,
  create: PropTypes.bool,
  update: PropTypes.bool,
  isLoading: PropTypes.bool,
  type: PropTypes.oneOf(['career', 'partner']).isRequired
}

Challenges.defaultProps = {
  create: false,
  update: false,
  isLoading: false
}

export default Challenges
